import React from 'react';
import { useSelector, useDispatch } from 'umi';
import { useMemoizedFn } from 'ahooks';
import NewMarkIcon from '@home/components/NewMarkIcon';
import { NEW_MARKS_NAMESPACE, INewMarksState } from '@intelligence/models/newMarks';
import { NewMarkDisplayMode, NewMarkType } from '@intelligence/enums/common';

import type { IDBIMenuVO } from '@intelligence/types/menu';
import type { INewMarkCfg } from '@intelligence/types/newMark';

type ICaclShowNewOfMenu = (
  menu: IDBIMenuVO,
  newMarksState: INewMarksState,
) => { showNew: boolean; newMarkType: NewMarkType };

type IAddTabNewIcon<T> = (tabs: T[], idsMap: Record<string, string>, newMarksState: INewMarksState) => T[];

type IAddNewMarkRecord = (payload: INewMarkCfg & { newMarkId: string }) => void;

/**
 * 判断当前菜单是否展示new标签
 * @param menu 菜单
 * @param newMarksState newMarks状态，菜单的newMarkId、tab的newMarkId 作为键，INewMarkType作为值 的map
 * @returns 是否展示标签、标签类型
 */
export const calcShowNewOfMenu: ICaclShowNewOfMenu = (menu, newMarksState) => {
  const menuId = menu?.id || '';
  const subMenus = menu?.subMenus || '';
  const menuNewMarkCfg = newMarksState?.[menuId] || {};
  const menuNewMarkDisplayMode = menuNewMarkCfg?.newMarkDisplayMode ?? NewMarkDisplayMode.RecordControlled;
  const menuNewMarkType = menuNewMarkCfg?.newMarkType ?? NewMarkType.New;
  const isFixedDisplay = menuNewMarkDisplayMode === NewMarkDisplayMode.Fixed;

  if (!subMenus?.length) {
    const showNew = !!menuId && Reflect.has(newMarksState, menuId);
    return { showNew: isFixedDisplay || showNew, newMarkType: menuNewMarkType };
  }

  const showNew = subMenus.some((subMenu) => {
    const { showNew } = calcShowNewOfMenu(subMenu, newMarksState);
    return showNew;
  });

  return { showNew: isFixedDisplay || showNew, newMarkType: menuNewMarkType };
};

export const addTabNewIcon: IAddTabNewIcon<any> = (tabs, idsMap, newMarksState) => {
  if (!Array.isArray(tabs)) return [];
  return tabs.map((tab) => {
    const tabNewMarkId = idsMap?.[tab?.id];
    const tabNewMarkCfg = newMarksState?.[tabNewMarkId];
    const tabNewMarkDisplayMode = tabNewMarkCfg?.newMarkDisplayMode ?? NewMarkDisplayMode.RecordControlled;
    const tabNewMarkType = tabNewMarkCfg?.newMarkType ?? NewMarkType.New;
    let labelTmp = tab?.label;
    if (tabNewMarkDisplayMode === NewMarkDisplayMode.Fixed || Reflect.has(newMarksState, tabNewMarkId)) {
      labelTmp = (
        <div className="dbi-tab-flex">
          {labelTmp}
          {/* @ts-ignore */}
          <NewMarkIcon type={tabNewMarkType} />
        </div>
      );
    }
    return {
      ...(tab || {}),
      newMarkDisplayMode: tabNewMarkDisplayMode,
      newMarkId: tabNewMarkId,
      newMarkType: tabNewMarkType,
      label: labelTmp,
    };
  });
};

export default function useNewMark() {
  const dispatch = useDispatch();

  const newMarksState = useSelector<any, INewMarksState>((state) => state?.[NEW_MARKS_NAMESPACE] || {});

  const addNewMarkRecord = useMemoizedFn<IAddNewMarkRecord>((payload) => {
    const { newMarkId } = payload || {};
    if (!newMarkId || !Reflect.has(newMarksState, newMarkId)) return;
    dispatch({
      type: `${NEW_MARKS_NAMESPACE}/addNewMarkRecord`,
      payload,
    });
  });

  return { newMarksState, addNewMarkRecord };
}
